import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ChatConversation, ChatMessage } from "../models/chat.model";

interface ChatbotState {
  conversations: ChatConversation[];
  activeId: string | null;
  createConversation: () => string;
  setActive: (id: string) => void;
  deleteConversation: (id: string) => void;
  addMessage: (conversationId: string, message: ChatMessage) => void;
  updateMessage: (
    conversationId: string,
    messageId: string,
    patch: Partial<ChatMessage>
  ) => void;
  setMessages: (conversationId: string, messages: ChatMessage[]) => void;
  renameConversation: (id: string, title: string) => void;
  clearAll: () => void;
}

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function emptyConversation(): ChatConversation {
  const now = Date.now();
  return {
    id: newId(),
    title: "Cuộc trò chuyện mới",
    messages: [],
    createdAt: now,
    updatedAt: now,
  };
}

export const useChatbotStore = create<ChatbotState>()(
  persist(
    (set, get) => ({
      conversations: [],
      activeId: null,

      createConversation: () => {
        const conv = emptyConversation();
        set((state) => ({
          conversations: [conv, ...state.conversations],
          activeId: conv.id,
        }));
        return conv.id;
      },

      setActive: (id) => set({ activeId: id }),

      deleteConversation: (id) =>
        set((state) => {
          const remaining = state.conversations.filter((c) => c.id !== id);
          return {
            conversations: remaining,
            activeId:
              state.activeId === id ? remaining[0]?.id ?? null : state.activeId,
          };
        }),

      addMessage: (conversationId, message) =>
        set((state) => ({
          conversations: state.conversations.map((c) =>
            c.id === conversationId
              ? {
                  ...c,
                  messages: [...c.messages, message],
                  updatedAt: Date.now(),
                  title:
                    c.messages.length === 0 && message.role === "user"
                      ? message.content.slice(0, 60)
                      : c.title,
                }
              : c
          ),
        })),

      updateMessage: (conversationId, messageId, patch) =>
        set((state) => ({
          conversations: state.conversations.map((c) =>
            c.id === conversationId
              ? {
                  ...c,
                  messages: c.messages.map((m) =>
                    m.id === messageId ? { ...m, ...patch } : m
                  ),
                  updatedAt: Date.now(),
                }
              : c
          ),
        })),

      setMessages: (conversationId, messages) =>
        set((state) => ({
          conversations: state.conversations.map((c) =>
            c.id === conversationId
              ? { ...c, messages, updatedAt: Date.now() }
              : c
          ),
        })),

      renameConversation: (id, title) =>
        set((state) => ({
          conversations: state.conversations.map((c) =>
            c.id === id ? { ...c, title } : c
          ),
        })),

      clearAll: () => set({ conversations: [], activeId: null }),
    }),
    {
      name: "dvn-chatbot",
      version: 6,
      migrate: (persistedState, version) => {
        const v = version ?? 0;
        let next = persistedState;
        if (v < 6) {
          const o = { ...(next as object) } as Record<string, unknown>;
          delete o.llmProvider;
          delete o.cerebrasModel;
          delete o.glmModel;
          next = o;
        }
        return next;
      },
    }
  )
);

export function makeChatMessage(
  role: ChatMessage["role"],
  content: string,
  extra?: Partial<ChatMessage>
): ChatMessage {
  return {
    id: newId(),
    role,
    content,
    createdAt: Date.now(),
    ...extra,
  };
}
